// Copyright (c) 2023 Ryan Piaskowy. All Rights Reserved.
import React, { useState, useEffect, useRef } from "react";
import Modal from "react-modal";
import { Link2, RotateCcw, Search, Share2, SquareArrowUp } from "lucide-react";
import "./ImageCalendar.css";
import config from "../config";
import MultiSelectFilter from "./MultiSelectFilter";
import WeekCalendar from "./WeekCalendar";
import EventImage from "./EventImage";
import EventLightbox from "./EventLightbox";
import { parseEventsTsv, parseRegionsTsv } from "../data/eventParser";

Modal.setAppElement("#root"); // Set the app root element for accessibility

// Tags that should always surface at the top of the tag picker.
const PRIORITY_TAGS = ["Staff Pick", "Event Feed"];
const LEVEL1_TAGS = ["Food Deals", "Alcohol Deals", "Something To Do"];
const LEVEL2_TAGS = ["Specific Dates", "Regularly Recurring"];
const INITIAL_WEEKS = 2;
const WEEK_INCREMENT = 2;
const MAX_WEEKS = 8;

const getInitialQueryState = () => {
  const params = new URLSearchParams(window.location.search);
  const parseList = (value) => (value ? value.split(",").filter(Boolean) : []);
  const requestedWeeks = Number(params.get("weeks"));

  return {
    region: params.get("region") || null,
    tags: parseList(params.get("tags")),
    venues: parseList(params.get("venues")),
    neighborhoods: parseList(params.get("neighborhoods")),
    weeks: [2, 4, 6, 8].includes(requestedWeeks) ? requestedWeeks : INITIAL_WEEKS,
    includeDuplicates: params.get("dupes") === "1",
  };
};

// Opens a Google search for the event so a viewer/admin can quickly research
// it themselves (venue hours, whether it's still happening, official pages, etc).
const buildLookItUpUrl = (event) => {
  const query = [event.region, event.eventVenue, event.eventTitle]
    .filter(Boolean)
    .join(" ");
  return `https://www.google.com/search?q=${encodeURIComponent(query)}`;
};

const initialQueryState = getInitialQueryState();

function ImageCalendar() {
  // Create state variables for caching
  const [cachedRegions, setCachedRegions] = useState([]);
  const [imageCache, setImageCache] = useState({});

  const [dayModal, setDayModal] = useState(null); // { date, events }
  const [regionEventData, setRegionEventData] = useState([]);
  const [eventData, setEventData] = useState([]);
  const [showMoreInfoModal, setShowMoreInfoModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [lightbox, setLightbox] = useState({ events: [], index: null });
  const [selectedRegion, setSelectedRegion] = useState(initialQueryState.region);
  const [regions, setRegions] = useState([]);
  const [distinctNeighborhoods, setDistinctNeighborhoods] = useState([]);
  const [distinctEventVenues, setDistinctEventVenues] = useState([]);
  const [distinctTags, setDistinctTags] = useState([]);
  const [selectedTags, setSelectedTags] = useState(initialQueryState.tags);
  const [selectedEventVenues, setSelectedEventVenues] = useState(initialQueryState.venues);
  const [selectedNeighborhoods, setSelectedNeighborhoods] = useState(initialQueryState.neighborhoods);
  const [weeksToShow, setWeeksToShow] = useState(initialQueryState.weeks);
  const [includeDuplicates, setIncludeDuplicates] = useState(initialQueryState.includeDuplicates);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isLoadingRegions, setIsLoadingRegions] = useState(true);
  const [dataError, setDataError] = useState("");
  const [linkCopied, setLinkCopied] = useState(false);
  const eventAbortController = useRef(null);

  useEffect(() => {
    const params = new URLSearchParams();
    if (selectedRegion) params.set("region", selectedRegion);
    if (selectedTags.length) params.set("tags", selectedTags.join(","));
    if (selectedEventVenues.length) params.set("venues", selectedEventVenues.join(","));
    if (selectedNeighborhoods.length) params.set("neighborhoods", selectedNeighborhoods.join(","));
    if (weeksToShow > INITIAL_WEEKS) params.set("weeks", String(weeksToShow));
    if (includeDuplicates) params.set("dupes", "1");

    const query = params.toString();
    window.history.replaceState(null, "", query ? `?${query}` : window.location.pathname);
  }, [selectedRegion, selectedTags, selectedEventVenues, selectedNeighborhoods, weeksToShow, includeDuplicates]);

  useEffect(() => {
    // Fetch regions and cache the result
    async function fetchRegions() {
      if (cachedRegions.length === 0) {
        try {
          setIsLoadingRegions(true);
          const response = await fetch(config.regionsTsvUrl, {
            headers: {
              "Cache-Control": "max-age=3600",
            },
          });
          if (!response.ok) throw new Error(`Regions request failed (${response.status})`);
          const textData = await response.text();
          const regionsArray = parseRegionsTsv(textData);
          setCachedRegions(regionsArray);
          setRegions(regionsArray.map((item) => item.region));
        } catch (error) {
          console.error("Error fetching regions:", error);
          setDataError("Regions are unavailable right now. Please try again.");
        } finally {
          setIsLoadingRegions(false);
        }
      } else {
        setRegions(cachedRegions.map((item) => item.region));
      }
    }

    if (!cachedRegions.length) {
      fetchRegions();
    }
  }, [cachedRegions]);

  useEffect(() => {
    if (selectedRegion) {
      fetchEventDataForRegion(selectedRegion);
    }
    return () => eventAbortController.current?.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRegion, cachedRegions]);

  async function fetchEventDataForRegion(region, forceRefresh = false) {
    const regionData = cachedRegions.find((item) => item.region === region);
    if (!regionData) {
      console.error("Region data not found for selected region:", region);
      return;
    }

    const { regionTsv } = regionData;

    if (imageCache[region] && !forceRefresh) {
      setRegionEventData(imageCache[region]);
      return;
    }

    eventAbortController.current?.abort();
    const controller = new AbortController();
    eventAbortController.current = controller;

    try {
      setIsRefreshing(true);
      setDataError("");
      const eventResponse = await fetch(regionTsv, {
        signal: controller.signal,
        headers: {
          "Cache-Control": "no-cache",
        },
      });
      if (!eventResponse.ok) throw new Error(`Events request failed (${eventResponse.status})`);
      const eventTextData = await eventResponse.text();
      const parsedEventData = parseEventsTsv(eventTextData, region);

      setImageCache((prev) => ({
        ...prev,
        [region]: parsedEventData,
      }));
      setRegionEventData(parsedEventData);
    } catch (error) {
      if (error.name === "AbortError") return;
      console.error("Error fetching event data:", error);
      setDataError(`Events for ${region} are unavailable right now. Please refresh.`);
    } finally {
      setIsRefreshing(false);
    }
  }

  // Recompute filter option lists and filtered events whenever the source data or filters change.
  useEffect(() => {
    // AND logic: an event must include every selected tag.
    const tagMatch = (event) =>
      selectedTags.length === 0 ||
      selectedTags.every((tag) => event.tags.includes(tag));

    // OR logic: an event matches if its neighborhood is ANY of the selected neighborhoods.
    const neighborhoodMatch = (event) =>
      selectedNeighborhoods.length === 0 ||
      selectedNeighborhoods.includes(event.neighborhood);

    // OR logic: an event matches if its venue is ANY of the selected venues.
    const eventVenueMatch = (event) =>
      selectedEventVenues.length === 0 ||
      selectedEventVenues.includes(event.eventVenue);

    // Default to hiding known duplicates; the checkbox opts back in.
    const duplicateMatch = (event) => includeDuplicates || !event.isDuplicate;

    const filteredEvents = regionEventData.filter(
      (event) =>
        tagMatch(event) &&
        neighborhoodMatch(event) &&
        eventVenueMatch(event) &&
        duplicateMatch(event)
    );

    setEventData(filteredEvents);

    // Each dropdown's option list is scoped by every OTHER active filter (but
    // not its own), so picking a tag narrows which venues/neighborhoods are
    // still offered, and picking a venue narrows the tags, and so on.
    // Already-selected values are always kept in the list even if they no
    // longer match, so a selection never silently disappears out from under
    // the user.
    const neighborhoods = Array.from(
      new Set(
        regionEventData
          .filter((event) => tagMatch(event) && eventVenueMatch(event) && duplicateMatch(event))
          .map((event) => event.neighborhood)
      )
    );
    setDistinctNeighborhoods(
      Array.from(new Set([...neighborhoods, ...selectedNeighborhoods])).sort()
    );

    const eventVenues = Array.from(
      new Set(
        regionEventData
          .filter((event) => tagMatch(event) && neighborhoodMatch(event) && duplicateMatch(event))
          .map((event) => event.eventVenue)
      )
    );
    setDistinctEventVenues(
      Array.from(new Set([...eventVenues, ...selectedEventVenues])).sort()
    );

    const allTags = regionEventData
      .filter((event) => neighborhoodMatch(event) && eventVenueMatch(event) && duplicateMatch(event))
      .reduce((tags, event) => {
        event.tags.forEach((tag) => tags.add(tag));
        return tags;
      }, new Set());
    setDistinctTags(
      Array.from(new Set([...allTags, ...selectedTags])).sort()
    );
  }, [selectedTags, selectedNeighborhoods, selectedEventVenues, includeDuplicates, regionEventData]);

  const handleMoreInfoClick = (image) => {
    setSelectedImage(image);
    setShowMoreInfoModal(true);
  };

  const closeMoreInfoModal = () => {
    setSelectedImage(null);
    setShowMoreInfoModal(false);
  };

  const openLightbox = (events, index) => {
    setLightbox({ events, index });
  };

  const closeLightbox = () => {
    setLightbox({ events: [], index: null });
  };

  const showDayEvents = (date, events) => {
    setDayModal({ date, events });
  };

  const closeDayModal = () => setDayModal(null);

  // Build the combined tag list with priority tags surfaced first.
  const combinedTags = [
    ...PRIORITY_TAGS.filter((tag) => distinctTags.includes(tag)),
    ...LEVEL1_TAGS.filter((tag) => distinctTags.includes(tag)),
    ...LEVEL2_TAGS.filter((tag) => distinctTags.includes(tag)),
    ...distinctTags.filter(
      (tag) =>
        !PRIORITY_TAGS.includes(tag) &&
        !LEVEL1_TAGS.includes(tag) &&
        !LEVEL2_TAGS.includes(tag)
    ),
  ];

  const handleRefresh = () => {
    if (selectedRegion) {
      fetchEventDataForRegion(selectedRegion, true);
    }
  };

  // Switch regions without reloading the whole app.
  const handleChangeRegion = () => {
    setSelectedRegion(null);
    setSelectedTags([]);
    setSelectedNeighborhoods([]);
    setSelectedEventVenues([]);
    setWeeksToShow(INITIAL_WEEKS);
    setIncludeDuplicates(false);
    setDayModal(null);
  };

  const resetFilters = () => {
    setSelectedTags([]);
    setSelectedNeighborhoods([]);
    setSelectedEventVenues([]);
    setIncludeDuplicates(false);
  };

  const copyViewLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setLinkCopied(true);
      window.setTimeout(() => setLinkCopied(false), 1800);
    } catch (error) {
      setDataError("Copying the view link is unavailable in this browser.");
    }
  };

  const shareView = async () => {
    if (!navigator.share) {
      await copyViewLink();
      return;
    }

    try {
      // Only pass `url` (no `text`) - most OS share sheets' "Copy" action
      // copies title/text + url concatenated together when text is present,
      // which meant "Copy" put "Events in Madison, WI <url>" on the
      // clipboard instead of just the link.
      await navigator.share({
        title: "MixIt.City",
        url: window.location.href,
      });
    } catch (error) {
      if (error.name !== "AbortError") {
        setDataError("Sharing this view is unavailable in this browser.");
      }
    }
  };

  const eventsByVenueForModal = {};
  (dayModal?.events || []).forEach((image) => {
    const venue = image.eventVenue || "Other";
    if (!eventsByVenueForModal[venue]) eventsByVenueForModal[venue] = [];
    eventsByVenueForModal[venue].push(image);
  });

  return (
    <div>
      {!dayModal && lightbox.index === null && !showMoreInfoModal && (
        <button
          className={`refresh-fab ${isRefreshing ? "spinning" : ""}`}
          onClick={handleRefresh}
          title="Refresh event data"
          aria-label="Refresh event data"
          disabled={!selectedRegion}
        >
          ⟳
        </button>
      )}

      <div>
        <button
          className="common-button"
          onClick={() => {
            if (selectedRegion) {
              const regionWithPlus = selectedRegion.replace(/\s+/g, "+");
              window.open(
                `${config.regionSubmissionFormUrl}${regionWithPlus}`,
                "_blank"
              );
            } else {
              window.open(config.regionSubmissionFormUrl, "_blank");
            }
          }}
        >
          Submit an Event
        </button>
      </div>

      {selectedRegion ? (
        <div>
          {dataError ? (
            <div className="status-panel status-panel--error" role="alert">
              <p>{dataError}</p>
              <button className="common-button" onClick={handleRefresh}>
                Try Again
              </button>
            </div>
          ) : isRefreshing && regionEventData.length === 0 ? (
            <div className="status-panel" role="status">
              Loading events...
            </div>
          ) : regionEventData.length > 0 ? (
            <div>
              <div className="calendar-actions">
                <button
                  className="calendar-action"
                  onClick={resetFilters}
                  title="Clear all filters"
                >
                  <RotateCcw aria-hidden="true" size={16} />
                  Reset filters
                </button>
                <button
                  className="calendar-action"
                  onClick={copyViewLink}
                  title="Copy a link to this calendar view"
                >
                  <Link2 aria-hidden="true" size={16} />
                  {linkCopied ? "Link copied" : "Copy link"}
                </button>
                <button
                  className="calendar-action share-view-button"
                  onClick={shareView}
                  title="Share this calendar view"
                >
                  <span className="share-icons" aria-hidden="true">
                    <SquareArrowUp size={16} />
                    <Share2 size={16} />
                  </span>
                  Share
                </button>
              </div>

              {config.featureFlags.showNeighborhoodFilter && (
                <div className="filter-row">
                  <MultiSelectFilter
                    label="Neighborhoods"
                    options={distinctNeighborhoods}
                    selected={selectedNeighborhoods}
                    onChange={setSelectedNeighborhoods}
                  />
                </div>
              )}

              <div className="filter-row">
                <MultiSelectFilter
                  label="Venues"
                  options={distinctEventVenues}
                  selected={selectedEventVenues}
                  onChange={setSelectedEventVenues}
                />
              </div>

              <div className="filter-row">
                <MultiSelectFilter
                  label="Tags"
                  options={combinedTags}
                  selected={selectedTags}
                  onChange={setSelectedTags}
                />
              </div>

              {config.featureFlags.showDuplicatesFilter && (
                <div className="filter-row duplicates-toggle-row">
                  <label className="duplicates-toggle">
                    <input
                      type="checkbox"
                      checked={includeDuplicates}
                      onChange={(e) => setIncludeDuplicates(e.target.checked)}
                    />
                    Include duplicates
                  </label>
                </div>
              )}

              <div className="calendar-hint">Select a day to see details</div>

              <WeekCalendar
                eventData={eventData}
                onShowDayEvents={showDayEvents}
                onViewImage={openLightbox}
                weeksToShow={weeksToShow}
              />

              {weeksToShow < MAX_WEEKS && (
                <button
                  className="common-button extend-calendar-button"
                  onClick={() =>
                    setWeeksToShow((current) =>
                      Math.min(current + WEEK_INCREMENT, MAX_WEEKS)
                    )
                  }
                >
                  Show {Math.min(WEEK_INCREMENT, MAX_WEEKS - weeksToShow)} more weeks
                </button>
              )}

              <Modal
                isOpen={!!dayModal}
                onRequestClose={closeDayModal}
                className="full-screen-modal"
                overlayClassName="overlay"
                // react-modal applies an inset "floating window" via inline
                // styles by default, which sits on top of our CSS and lets
                // the calendar (and its badges) show through around it.
                // Override those inline defaults so the modal truly fills
                // the screen and sits above everything else.
                style={{
                  overlay: { zIndex: 2000 },
                  content: {
                    position: "fixed",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    border: "none",
                    background: "rgba(0, 0, 0, 0.9)",
                    padding: 0,
                  },
                }}
              >
                <div className="modal-content">
                  <h2 className="modal-date-heading">
                    {dayModal?.date?.toLocaleDateString("en-US", {
                      weekday: "long",
                      month: "long",
                      day: "numeric",
                    })}
                  </h2>
                  {Object.entries(eventsByVenueForModal).map(
                    ([venue, events], index) => (
                      <div key={index} className="event-venue-container-modal">
                        <h3>{venue}</h3>
                        {events.map((image, innerIndex) => (
                          <div key={innerIndex} className="modal-image-container">
                            {image.imageUrl ? (
                              <EventImage
                                event={image}
                                className="modal-image-wrapper"
                                onView={() => openLightbox([image], 0)}
                              />
                            ) : (
                              <div className="poster-modal">
                                <div className="poster-content">
                                  {`${image.eventTitle}: ${image.eventNotes}`.slice(
                                    0,
                                    500
                                  )}
                                </div>
                              </div>
                            )}

                            <div className="button-container">
                              {image.imageUrl ? (
                                <button
                                  className="common-button"
                                  onClick={() => openLightbox([image], 0)}
                                >
                                  View Flyer
                                </button>
                              ) : null}
                              {image.eventWebsite ||
                              image.eventTitle ||
                              image.eventNotes ||
                              image.startTime ||
                              image.tags ||
                              image.endTime ? (
                                <button
                                  className="common-button"
                                  onClick={() => handleMoreInfoClick(image)}
                                >
                                  More Info
                                </button>
                              ) : null}
                              {image.eventVenue || image.eventTitle ? (
                                <a
                                  href={buildLookItUpUrl(image)}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="common-button"
                                  title="Search the web for this event to verify details yourself"
                                >
                                  <Search aria-hidden="true" size={16} />
                                  Look It Up
                                </a>
                              ) : null}
                              <a
                                href={`${config.updateEventFormUrl}${encodeURIComponent(
                                  image.id
                                )}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="common-button"
                              >
                                Update
                              </a>
                            </div>
                          </div>
                        ))}
                      </div>
                    )
                  )}
                </div>
                <button className="close-button" onClick={closeDayModal}>
                  Close
                </button>
              </Modal>

              <EventLightbox
                events={lightbox.events}
                index={lightbox.index}
                onClose={closeLightbox}
              />

              {showMoreInfoModal && selectedImage && (
                <Modal
                  isOpen={true}
                  onRequestClose={closeMoreInfoModal}
                  className="mini-modal-content"
                  overlayClassName="mini-modal"
                  style={{
                    overlay: { zIndex: 3000 },
                    content: {
                      position: "relative",
                      inset: "auto",
                    },
                  }}
                >
                    {selectedImage.eventVenue ? (
                      <h2>{selectedImage.eventVenue}</h2>
                    ) : (
                      <h2>More Info</h2>
                    )}
                    {selectedImage.eventWebsite && (
                      <p>
                        Website:{" "}
                        <a
                          href={selectedImage.eventWebsite}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {selectedImage.eventWebsite}
                        </a>
                      </p>
                    )}
                    {selectedImage.eventTitle && (
                      <p>Title: {selectedImage.eventTitle}</p>
                    )}
                    {selectedImage.eventNotes && (
                      <div>
                        <p>Notes:</p>
                        <pre>{selectedImage.eventNotes.replace(/~\|/g, "\n")}</pre>
                      </div>
                    )}
                    {selectedImage.tags && (
                      <p>Tags: {selectedImage.tags.join(", ")}</p>
                    )}
                    {selectedImage.startTime && (
                      <p>Start Time: {selectedImage.startTime}</p>
                    )}
                    {selectedImage.endTime && <p>End Time: {selectedImage.endTime}</p>}
                    <button className="close-button" onClick={closeMoreInfoModal}>
                      Close
                    </button>
                </Modal>
              )}
              <br />
            </div>
          ) : (
            <div className="status-panel" role="status">
              No events match these filters.
            </div>
          )}
        </div>
      ) : (
        <div>
          {isLoadingRegions ? (
            <p role="status">Loading regions...</p>
          ) : regions.length > 0 ? (
            <div>
              <h2>Select a Region</h2>
              <select
                value={selectedRegion || ""}
                onChange={(e) => setSelectedRegion(e.target.value)}
              >
                <option value="">-- Select a Region --</option>
                {regions.map((region) => (
                  <option key={region} value={region}>
                    {region}
                  </option>
                ))}
              </select>
            </div>
          ) : (
            <div className="status-panel status-panel--error" role="alert">
              <p>{dataError || "Regions are unavailable right now."}</p>
              <button className="common-button" onClick={() => window.location.reload()}>
                Try Again
              </button>
            </div>
          )}
        </div>
      )}
      {selectedRegion && (
        <div className="change-region-row">
          <button className="common-button secondary" onClick={handleChangeRegion}>
            Change Region ({selectedRegion})
          </button>
        </div>
      )}
      <div className="footer">
        <p>
          Questions? Want to get involved? Contact{" "}
          <a href="mailto:MixItCity@gmail.com">MixItCity@gmail.com</a>
        </p>
      </div>
      <button
        className="app-reload-button"
        onClick={() => window.location.reload()}
        title="Reload the entire app"
      >
        Reload app
      </button>
    </div>
  );
}

export default ImageCalendar;
